import { Component, ElementRef, OnDestroy, OnInit, signal, ViewChild, ViewContainerRef } from '@angular/core';
import {MapFactoryService, PositionEnum} from '_@api/map-service/map-factory.service';
import {NgClass, NgOptimizedImage, NgStyle} from "@angular/common";
import {MatCheckbox} from "@angular/material/checkbox";
import {FormsModule} from "@angular/forms";
import {Constants} from "_@shared/models/constants";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import Point from "@arcgis/core/geometry/Point";
import {IQueryFeaturesOptions} from "@esri/arcgis-rest-feature-service";
import {DialogComponent} from "_@shared/components/display/dialog/dialog-component/dialog.component";
import {filter, from, map, Subscription, switchMap, tap} from "rxjs";
import {BreakpointObserver, Breakpoints, BreakpointState} from "@angular/cdk/layout";
import {ToasterService} from "_@shared/services/toaster.service";
import {ApiService} from "_@api/api.service";
import {ServiceCenterService} from "_@api/layers/service-center.service";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import {PopupField, PopupService} from "_@api/map-service/popup.service";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatButtonModule} from '@angular/material/button';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  imports: [
    NgOptimizedImage,
    MatCheckbox,
    FormsModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    NgClass,
    NgStyle,
  ],
  standalone: true,
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('viewDiv', { static: true }) private elementRef: ElementRef;
  belowNormalOperationsLayerToggle = true;
  svcLayerToggle = false;
  weatherWarningsLayerToggle = true;

  protected isScreenSmall: boolean;
  protected isScreenXSmall: boolean;

  private breakpointSubscription: Subscription;

  private weatherLayer: MapImageLayer;
  private svcStatusOKGraphicLayer: GraphicsLayer;
  private svcWithWarningsLayer: GraphicsLayer;

  private legendButton: HTMLSpanElement;
  protected isLegendClosed = false;
  svcLayersLoaded = signal(false);

  constructor(
    private mapFactory: MapFactoryService,
    private viewContainerRef: ViewContainerRef,
    private breakpointObserver: BreakpointObserver,
    private apiService: ApiService,
    private toasterService: ToasterService,
    private serviceCenterService: ServiceCenterService,
    private popupService: PopupService
  ) {}

  private readonly SMALL_SCREEN_ZOOM = 3;
  private readonly X_SMALL_SCREEN_ZOOM = 2;

  ngOnInit() {
    this.breakpointSubscription = this.breakpointObserver
      .observe([Breakpoints.Small, Breakpoints.XSmall])
      .subscribe((state: BreakpointState) => {
        const breakpoints = state.breakpoints;
        this.isScreenXSmall = !!breakpoints[Breakpoints.XSmall];
        this.isScreenSmall = !!breakpoints[Breakpoints.Small];
        this.isLegendClosed = this.isScreenSmall || this.isScreenXSmall;
      });

    this.mapFactory.initializeMapView(this.elementRef);

    this.mapFactory
      .getMapView()
      .when()
      .then(() => {
        if (this.isScreenSmall || this.isScreenXSmall) {
          this.mapFactory.addCenterButton(this.mapFactory.getMapView(), {
            center: this.mapFactory.initialView.center,
            zoom: this.isScreenSmall ? this.SMALL_SCREEN_ZOOM : this.X_SMALL_SCREEN_ZOOM,
          });
        } else {
          this.mapFactory.addCenterButton(this.mapFactory.getMapView());
        }

        this.isScreenSmall || this.isScreenXSmall ? this.addLegendButton() : this.removeLegendButton();

        this.weatherLayer = new MapImageLayer({
          id: Constants.WEATHER_LAYER_ID,
          url: Constants.WEATHER_LAYER_URL,
          opacity: 0.7,
        });

        // add weather layer to map
        this.mapFactory.addLayer(this.weatherLayer);

        // add service center layer to map
        this.apiService.getServiceCenterStatus().subscribe({
          next: statuses => {
            if (statuses && statuses.length > 0) {
              this.serviceCenterService.loadLayer(statuses).then(svcLayers => {
                this.svcStatusOKGraphicLayer = svcLayers[0];
                this.svcWithWarningsLayer = svcLayers[1];

                if ((this.isScreenSmall || this.isScreenXSmall) && this.svcWithWarningsLayer.graphics.length > 0) {
                  this.mapFactory.expandByGraphics(this.svcWithWarningsLayer.graphics, 1);
                } else {
                  if (this.isScreenSmall) {
                    this.mapFactory.getMapView().zoom = this.SMALL_SCREEN_ZOOM;
                  }
                  if (this.isScreenXSmall) {
                    this.mapFactory.getMapView().zoom = this.X_SMALL_SCREEN_ZOOM;
                  }
                }

                this.svcStatusOKGraphicLayer.visible = this.svcLayerToggle;
                this.svcWithWarningsLayer.visible = this.weatherWarningsLayerToggle;

                this.mapFactory.addLayer(this.svcWithWarningsLayer);
                this.mapFactory.addLayer(this.svcStatusOKGraphicLayer);

                this.svcLayersLoaded.set(true);
              });
            } else {
              this.toasterService.showInfo('No service centers found!', 3000);
            }
          },
          error: error => {
            console.error('Error fetching truck locations:', error);
            this.toasterService.showError('Failed to fetch service centers. Please try again.', 5000);
          },
        });

        this.mapFactory.getMapView().on('click', async event => {
          event.native?.preventDefault?.();
          event.native?.stopPropagation?.();

          const hit = await this.mapFactory.getMapView().hitTest(event, {
            include: [this.weatherLayer, this.svcStatusOKGraphicLayer, this.svcWithWarningsLayer],
          });

          const hitLayerId = hit.results[0]?.layer?.id;
          switch (hitLayerId) {
            case Constants.SERVICE_CENTERS_STATUS_OK_LAYER_ID:
              await this.handleClicksOnOKService(
                event.mapPoint,
                this.graphicsToFeatureLayer(this.svcStatusOKGraphicLayer)
              );
              break;
            case Constants.SERVICE_CENTERS_STATUS_WARNINGS_LAYER_ID:
              await this.handleClicksOnWarningService(
                event.mapPoint,
                this.graphicsToFeatureLayer(this.svcWithWarningsLayer)
              );
              break;
            default:
              let queryOptions: IQueryFeaturesOptions = {
                url: Constants.WEATHER_WARNING_LAYER_URL,
                returnGeometry: true,
                outFields: ['prod_type', 'url'],
                outSR: {
                  wkid: 102100,
                },
              };
              this.handleClicksOnWeather(event.mapPoint, queryOptions);
              break;
          }
        });
      });
  }

  private handleClicksOnWeather(mapPoint: Point, queryOptions: IQueryFeaturesOptions) {
    this.mapFactory.queryLayerByUrl(queryOptions, mapPoint).then(featureSet => {
      const features = featureSet['features'];
      if (features.length > 0) {
        if (!this.isLegendClosed && (this.isScreenXSmall || this.isScreenSmall)) {
          this.isLegendClosed = true;
        }
        const graphic = features[0];

        this.showPopup(mapPoint, 'NWS Warning Detail', [
          { label: 'Event', type: 'text', value: graphic.attributes.prod_type },
          { label: 'More Info', type: 'link', urlText: 'NWS Bulletin', href: graphic.attributes.url },
        ]);
      }
    });
  }

  private async handleClicksOnOKService(mapPoint: Point, featureLayer: FeatureLayer): Promise<void> {
    from(this.mapFactory.queryLayerByLayer(featureLayer, this.mapFactory.getBufferPolygonByMapPoint(mapPoint)))
      .pipe(
        map((featureSet: any) => {
          const feature = featureSet?.features?.find(feature => feature.attributes.sca) ?? null;
          return feature?.attributes?.sca as string | undefined;
        }),
        filter((sca): sca is string => !!sca),
        switchMap(sca => this.apiService.getServiceCenterWeatherForecast(sca)),
        tap((forecast: any) => {
          if (!this.isLegendClosed && (this.isScreenXSmall || this.isScreenSmall)) {
            this.isLegendClosed = true;
          }

          this.showPopup(mapPoint, 'Weather Forecast', [
            { label: 'S/C', type: 'text', value: forecast.sc },
            { label: 'Date', type: 'text', value: forecast.date },
          ]);
        })
      )
      .subscribe();
  }

  private async handleClicksOnWarningService(mapPoint: Point, featureLayer: FeatureLayer): Promise<void> {
    from(this.mapFactory.queryLayerByLayer(featureLayer, this.mapFactory.getBufferPolygonByMapPoint(mapPoint)))
      .pipe(
        map((featureSet: any) => {
          return featureSet?.features?.find(f => f.attributes?.sca);
        }),
        tap((feature: any) => {
          if (!this.isLegendClosed && (this.isScreenXSmall || this.isScreenSmall)) {
            this.isLegendClosed = true;
          }

          this.showPopup(mapPoint, 'Service Details', [
            { label: 'Svc Center', type: 'text', value: feature.attributes.name },
            { label: 'Alpha Code', type: 'text', value: feature.attributes.sca },
            { label: 'Date', type: 'text', value: feature.attributes.date },
            { label: 'Reason', type: 'text', value: feature.attributes.cat },
            { label: 'Notes', type: 'text', value: feature.attributes.notes },
            { label: 'Operating at', type: 'text', value: feature.attributes.percent + feature.attributes.text },
          ]);
        })
      )
      .subscribe();
  }

  private showPopup(mapPoint: Point, title: string, fields: PopupField[]) {
    this.popupService.openPopup({
      viewContainerRef: this.viewContainerRef,
      view: this.mapFactory.getMapView(),
      location: new Point({
        x: mapPoint.x,
        y: mapPoint.y,
        spatialReference: this.mapFactory.getMapView().spatialReference,
      }),
      componentType: DialogComponent,
      title: title,
      fields: fields,
    });
  }

  ngOnDestroy(): void {
    this.breakpointSubscription.unsubscribe();
  }

  protected readonly Constants = Constants;

  toggleLayers(layerId: string) {
    switch (layerId) {
      case Constants.SERVICE_CENTERS_STATUS_OK_LAYER_ID:
        if (this.svcWithWarningsLayer) {
          this.svcStatusOKGraphicLayer.visible = this.svcLayerToggle;
        }
        break;
      case Constants.SERVICE_CENTERS_STATUS_WARNINGS_LAYER_ID:
        this.svcWithWarningsLayer.visible = this.weatherWarningsLayerToggle;
        break;
      case Constants.WEATHER_LAYER_ID:
        this.weatherLayer.visible = !this.weatherLayer.visible;
    }
  }

  private graphicsToFeatureLayer(graphicsLayer: GraphicsLayer): FeatureLayer {
    return new FeatureLayer({
      source: graphicsLayer.graphics.toArray(),
      fields: this.serviceCenterService.featureLayerFields,
      objectIdField: 'ObjectID',
      geometryType: 'point',
      spatialReference: this.mapFactory.getMapView().spatialReference,
    });
  }

  private addLegendButton() {
    this.legendButton = document.createElement('span');
    this.legendButton.className = 'esri-widget--button esri-widget esri-interactive';
    this.legendButton.innerHTML = '<i class="esri-icon-legend"></i>';

    this.legendButton.addEventListener('click', () => {
      document.getElementById('legend').style.display = this.isLegendClosed ? 'block' : 'none';
      this.isLegendClosed = !this.isLegendClosed;

      if (!this.isLegendClosed) {
        this.mapFactory.getMapView().closePopup();
      }
    });

    this.mapFactory.addToMapView(this.legendButton, PositionEnum.TOP_RIGHT);
  }

  private removeLegendButton() {
    this.mapFactory.removeFromMapView(this.legendButton);
  }
}
